import { ModalSystem } from './ModalSystem';
import { Logger } from './Logger';
import { parseURLHash, type ParsedURL } from './URLDataParser';
import { userSettingsService, SettingScope } from './UserSettingsService';

const logger = Logger.scope('NavigationHistory');

/**
 * NavigationHistory - Manages form navigation history with browser integration
 */

export interface HistoryEntry {
  formName: string;
  title: string;
  icon?: string;
  timestamp: number;
}

export class NavigationHistory {
  private static readonly MAX_HISTORY = 25;
  private static readonly STORAGE_KEY = 'navigation.history';
  private static history: HistoryEntry[] = [];
  private static onNavigateCallback?: (formName: string) => void;
  private static onExitCallback?: () => void;
  private static isInitialized = false;
  private static isExiting = false;
  private static appEntryHistoryLength = 0;
  private static exitWarningEnabled = true;

  /**
   * Initialize the navigation history system
   */
  public static init(onNavigate: (formName: string) => void, onExit?: () => void): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    
    this.onNavigateCallback = onNavigate;
    this.onExitCallback = onExit;

    // Load persisted history from storage
    this.loadFromStorage();

    // Record the browser history length at app entry point
    // This helps us detect when user goes back beyond app entry
    this.appEntryHistoryLength = window.history.length;
    logger.debug('App entry history length:', this.appEntryHistoryLength);

    // Clear any existing state and start fresh
    // This ensures we have a clean entry point even if browser restored old state
    const currentState = window.history.state;
    logger.debug('Current state on init:', currentState);
    
    // Set entry point WITHOUT isAppState marker
    // This will trigger exit prompt when user goes back to it
    window.history.replaceState({ isEntryPoint: true }, '', window.location.href);
    logger.debug('Set entry point (not app state)');

    // Handle browser back/forward buttons
    window.addEventListener('popstate', (event) => {
      logger.debug('popstate event', event.state, 'history.length:', window.history.length);
      
      // Check if user went back beyond app entry point
      if (!event.state || !event.state.isAppState) {
        if (!this.exitWarningEnabled) {
          logger.debug('Exit warning disabled, allowing exit');
          // If we are at the entry point, go back one more time to actually leave
          if (event.state && event.state.isEntryPoint) {
             window.history.back();
          }
          return;
        }

        logger.debug('User attempting to leave application (outside app state)');
        this.handleExitAttempt();
        return;
      }

      // If we have a formName, navigate to it
      if (event.state.formName) {
        if (this.onNavigateCallback) {
          this.onNavigateCallback(event.state.formName);
        }
      } else {
        // No form name but still app state - might be initial load
        logger.debug('App state without form name');
      }
    });

    logger.debug('Initialized');
  }

  /**
   * Enable or disable the exit warning (e.g. disable on login screen)
   */
  public static setExitWarningEnabled(enabled: boolean): void {
    this.exitWarningEnabled = enabled;
    logger.debug('Exit warning enabled:', enabled);
  }

  /**
   * Reset history and browser state (e.g. on logout)
   */
  public static reset(): void {
    this.clear();
    
    // Reset browser state to entry point
    // We remove the hash to ensure we are at the root
    const url = window.location.pathname + window.location.search;
    window.history.replaceState({ isEntryPoint: true }, '', url);
    logger.debug('History reset to entry point');
  }

  /**
   * Add a form to navigation history
   */
  public static push(formName: string, title?: string): void {
    const entry: HistoryEntry = {
      formName,
      title: title || this.formatTitle(formName),
      timestamp: Date.now()
    };

    // Remove exact duplicate if it exists (same form + same params = same record)
    // This allows footballer?id=123 and footballer?id=456 to coexist
    this.history = this.history.filter(h => h.formName !== formName);

    // Add to front of history
    this.history.unshift(entry);

    // Trim to max size
    if (this.history.length > this.MAX_HISTORY) {
      this.history = this.history.slice(0, this.MAX_HISTORY);
    }

    // Persist to storage
    this.saveToStorage();

    // Add to browser history with app state marker
    const state = { formName, isAppState: true };
    const url = `#${formName}`;
    const currentState = window.history.state;
    
    // Check if we're already on this form
    if (currentState?.formName === formName && currentState?.isAppState) {
      // Already on this form with app state - nothing to do
      logger.debug('Already on form with app state', formName);
    } else {
      // Push new form state (entry point is behind us)
      window.history.pushState(state, '', url);
      logger.debug('Pushed to browser history', { formName, url });
    }

    // Trigger UI update
    this.notifyHistoryChanged();
  }

  /**
   * Handle exit attempt - show confirmation modal
   */
  private static handleExitAttempt(): void {
    if (this.isExiting) return; // Prevent multiple prompts
    this.isExiting = true;

    ModalSystem.questionYesNo(
      'You are about to leave the application. Any unsaved changes will be lost.',
      '⚠️ Leaving Application',
      () => {
        // User confirmed exit
        logger.debug('User confirmed exit');
        
        // Call exit callback (for future logout/cleanup)
        if (this.onExitCallback) {
          this.onExitCallback();
        }

        // Clear history
        this.clear();
        
        // Actually navigate away (back to previous page or close tab)
        window.history.back();
        
        this.isExiting = false;
      },
      () => {
        // User cancelled - stay in app
        logger.debug('User cancelled exit');
        
        // Determine current form
        let currentForm: string | null = null;
        if (this.history.length > 0) {
          currentForm = this.history[0].formName;
        } else {
          currentForm = this.getCurrentFormFromURL();
          if (!currentForm) {
            currentForm = 'dashboard'; // fallback
          }
        }
        
        // Push forward to stay in the app with app state marker
        window.history.pushState({ formName: currentForm, isAppState: true }, '', `#${currentForm}`);
        logger.debug('Pushed forward state to stay in app', currentForm);
        
        this.isExiting = false;
      },
      'Leave',
      'Stay'
    );
  }

  /**
   * Get the navigation history
   */
  public static getHistory(): HistoryEntry[] {
    return [...this.history];
  }

  /**
   * Clear navigation history
   */
  public static clear(): void {
    this.history = [];
    this.saveToStorage();
    this.notifyHistoryChanged();
  }

  /**
   * Load history from persistent storage
   */
  private static loadFromStorage(): void {
    try {
      const stored = userSettingsService.get<HistoryEntry[]>(this.STORAGE_KEY);
      if (stored && Array.isArray(stored)) {
        // Filter out entries older than 7 days to keep history relevant
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        this.history = stored
          .filter(entry => entry.timestamp > sevenDaysAgo)
          .slice(0, this.MAX_HISTORY);
        logger.debug('Loaded history from storage:', this.history.length, 'entries');
      }
    } catch (error) {
      logger.warn('Failed to load history from storage:', error);
      this.history = [];
    }
  }

  /**
   * Save history to persistent storage
   */
  private static saveToStorage(): void {
    try {
      userSettingsService.set(this.STORAGE_KEY, this.history, SettingScope.LOCAL).catch(error => {
        logger.warn('Failed to save history to storage:', error);
      });
    } catch (error) {
      logger.warn('Failed to save history to storage:', error);
    }
  }

  /**
   * Format form name into readable title
   */
  private static formatTitle(formName: string): string {
    return formName
      .split(/(?=[A-Z])|[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Notify listeners that history changed
   */
  private static notifyHistoryChanged(): void {
    // Dispatch custom event for UI components to listen to
    window.dispatchEvent(new CustomEvent('navigationHistoryChanged', {
      detail: { history: this.getHistory() }
    }));
  }

  /**
   * Navigate back in browser history
   */
  public static back(): void {
    window.history.back();
  }

  /**
   * Navigate forward in browser history
   */
  public static forward(): void {
    window.history.forward();
  }

  /**
   * Update the title and icon of the current (most recent) history entry
   * Call this after loading entity data to show a human-readable title
   * 
   * @param title - The new title, e.g., "Footballer: Bethany England"
   * @param icon - Optional icon class, e.g., "fas fa-user text-primary"
   */
  public static updateCurrentEntry(title: string, icon?: string): void {
    if (this.history.length === 0) {
      logger.warn('Cannot update entry - history is empty');
      return;
    }
    
    this.history[0].title = title;
    if (icon) {
      this.history[0].icon = icon;
    }
    logger.debug('Updated current history entry:', { title, icon });
    
    // Persist to storage
    this.saveToStorage();
    
    // Notify listeners so dropdown updates
    this.notifyHistoryChanged();
  }

  /**
   * Update the title of the current (most recent) history entry
   * @deprecated Use updateCurrentEntry instead
   */
  public static updateCurrentTitle(title: string): void {
    this.updateCurrentEntry(title);
  }

  /**
   * Get current form name from URL hash (strips any ?data= params)
   */
  public static getCurrentFormFromURL(): string | null {
    const hash = window.location.hash.substring(1); // Remove #
    if (!hash) return null;
    
    // Parse to extract just the form name (before any ?data= params)
    const parsed = parseURLHash(hash);
    return parsed.formName || null;
  }

  /**
   * Get parsed URL data from current hash (includes formName and decoded data)
   */
  public static getParsedURL(): ParsedURL {
    return parseURLHash(window.location.hash);
  }
}
